import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Countdown } from '@liveauctioneers/hammer-ui-core/countdown';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { CatalogRegistrationMessagesExperiment } from '@/components/CatalogBannerExperiment/BannerActions/CatalogRegistrationMessagesExperiment';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import {
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogIsTimedPlusClosing,
    getCatalogStartTime,
    getCatalogTimedPlusBiddingEndsFromTs,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsRegistered,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    catalogId: number;
    className?: string;
    setForceIsLive: (force: boolean) => void;
};

// Mobile/inline-layout counterpart to TimedPlusAuctionStatus, split out into its own component
// (rather than branching the sidebar version on a `layout` prop) so the Figma mobile card can
// evolve independently of the desktop sidebar without conditionals threaded through shared JSX.
// Figma's mobile card has no heading/status label, progress bar, or "About Timed+" link — those
// are sidebar-only.
export const TimedPlusAuctionStatusMobile = ({ catalogId, className, setForceIsLive }: Props) => {
    const { variants } = useAmplitudeExperiments();
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const reloadPageData = useCallback(() => {
        dispatch(loadPageData({ amplitudeExperimentFlags: variants, featureFlags, location }));
    }, [dispatch, location, variants, featureFlags]);
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));
    const biddingEndsFrom = useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId));
    const biddingOpens = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const lotsAreClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    };

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start gap-xs', className)}
            data-testid="timed-plus-auction-status"
            suppressHydrationWarning
        >
            {!lotsAreClosing && (
                // Figma's mobile card has no visible countdown, but the Countdown's onComplete is
                // what flips the page to "live"/bidding-open and reloads data once the timer runs
                // out — stay mounted (just hidden) instead of unmounting, or a mobile visitor
                // sitting on the page never sees it update without a manual refresh.
                <div
                    data-testid="countdown"
                    hidden
                >
                    <Countdown
                        completionTime={(timedPlusBiddingOpen ? biddingEndsFrom : biddingOpens) * 1000}
                        forceAllUnits
                        onComplete={() => {
                            setForceIsLive(true);
                            reloadPageData();
                        }}
                        small
                        timeLabels={{ days: ' days', hours: ' hrs', minutes: ' mins', seconds: ' secs' }}
                    />
                </div>
            )}
            {suspended ? (
                <Button
                    className="mb-0 w-full"
                    data-testid="catalogAccountSuspendedButton"
                    disabled
                    variant="filled"
                >
                    {formatMessage(['account_suspended'])}
                </Button>
            ) : (
                !registered && (
                    <Button
                        className="mb-0 w-full"
                        data-testid="liveCatalogRegisterButton"
                        onClick={registerForCatalog}
                        style={isWhiteLabel ? 'primary' : 'critical'}
                        variant="filled"
                    >
                        {formatMessage(['register_for_auction'])}
                    </Button>
                )
            )}
            {registered && (
                <CatalogRegistrationMessagesExperiment
                    catalogId={catalogId}
                    className="w-full"
                />
            )}
        </div>
    );
};
